/**
 * THE BROWSER'S HALF OF "A FRESH PROOF BEFORE A HIGH-RISK ACTION" (MFA build plan, Stage 7).
 *
 * The server refuses a high-risk action with 403 `step_up_required` (or
 * `step_up_stronger_required`) unless the request carries, in X-Step-Up, the five-minute token
 * earned by proving a device. This module holds that token IN MEMORY ONLY - never in storage,
 * where a script or the next person at the keyboard could reuse it - and is how the request
 * layer asks the user for a proof when the server wants one (axios.ts).
 *
 * The asking itself is a screen (features/devices StepUpGate, mounted once at the root), which
 * registers here. With no screen registered - a test, or a page with no gate - nobody can be
 * asked and the refusal simply reaches the caller.
 */
export const STEP_UP_HEADER = 'X-Step-Up'
/** What the server says when it wants a proof. */
export const STEP_UP_CODES = ['step_up_required', 'step_up_stronger_required'] as const

interface Held { token: string; until: number; strong: boolean }
let held: Held | null = null
let asker: ((strongOnly: boolean) => Promise<{ token: string; expiresInSeconds: number; strong: boolean } | null>) | null = null

/** Keep a proof just earned. A little short of the server's five minutes, so it is never sent as it expires. */
export function holdStepUp(token: string, expiresInSeconds: number, strong: boolean, now = Date.now()) {
    held = { token, until: now + Math.max(0, expiresInSeconds - 15) * 1000, strong }
}
/** The proof in hand, if it is still good - and strong, when only a strong one will do. */
export function currentStepUp(strongOnly = false, now = Date.now()): string | null {
    if (!held || held.until <= now) { held = null; return null }
    return strongOnly && !held.strong ? null : held.token
}
export function forgetStepUp() { held = null }

/** The screen that can ask the user registers itself; returns how to unregister. */
export function registerStepUpAsker(fn: NonNullable<typeof asker>) {
    asker = fn
    return () => { if (asker === fn) asker = null }
}

/** Was this refusal the server asking for a proof? Returns which kind, or null. */
export function stepUpWanted(error: unknown): 'any' | 'strong' | null {
    const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response
    if (response?.status !== 403) return null
    if (response.data?.error === 'step_up_stronger_required') return 'strong'
    return response.data?.error === 'step_up_required' ? 'any' : null
}

/**
 * Ask the user to prove a device. Resolves with the token to send, or null if they could not or
 * would not (or there is nobody to ask).
 */
export async function askForStepUp(strongOnly: boolean): Promise<string | null> {
    const inHand = currentStepUp(strongOnly)
    if (inHand) return inHand
    if (!asker) return null
    const earned = await asker(strongOnly)
    if (!earned) return null
    holdStepUp(earned.token, earned.expiresInSeconds, earned.strong)
    return currentStepUp(strongOnly)
}
